import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Smartphone, Laptop, ArrowLeft, Home as HomeIcon, CheckCircle2, Loader2, AlertCircle, Send } from "lucide-react";
import { Seo, serviceJsonLd } from "../components/Seo";

const MOBILE_INCLUDED = [
  "Alla dina appar, inklusive Mobilt BankID och bankappar",
  "Alla kontakter, bilder, videor, filer och viktig information",
  "Personlig hjälp så att allt fungerar direkt på din nya mobil",
];

const COMPUTER_INCLUDED = [
  "Säker överföring av alla dina personliga filer, dokument, bilder och videor",
  "Installation och flytt av dina viktiga program (t.ex. BankID, webbläsare, Office-paketet och e-post)",
  "Personlig genomgång så att du hittar allt på din nya dator",
];

export default function Mobile() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", phone_models: "", service: "", message: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.phone_models.trim()) {
      setError("Fyll i namn, telefonnummer och dina modeller.");
      return;
    }
    if (!form.service) { setError("Välj vilken tjänst du vill boka."); return; }
    if (!validEmail) { setError("Skriv in en giltig e-postadress."); return; }
    setSubmitting(true);
    try {
      await api.post("/mobile", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Något gick fel. Försök igen om en stund.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title="Enkelt enhetsbyte – ny mobil eller dator"
        description="Har du köpt en ny telefon eller dator? Vi flyttar över allt tryggt och säkert – appar, BankID, kontakter, bilder och filer. Fast pris 599 kr."
        path="/enhetsbyte"
        jsonLd={serviceJsonLd({ name: "Enkelt enhetsbyte", description: "Överföring av data och appar till ny mobil eller dator.", path: "/enhetsbyte", price: 599 })}
      />
      <Link to="/" data-testid="mobile-back" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka till start
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6 flex items-start gap-3">
        <span className="flex gap-1 shrink-0 mt-1 text-[#0056D2]"><Smartphone size={40} strokeWidth={2.5} /><Laptop size={40} strokeWidth={2.5} /></span>
        Enkelt enhetsbyte – flytta tryggt till din nya mobil eller dator
      </h1>

      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Har du köpt en ny telefon eller en ny dator?
      </p>
      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Jag hjälper dig att flytta över allt tryggt och säkert – till både din nya mobil och din nya dator.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-8">
        {/* Mobilbytet */}
        <div data-testid="service-mobile" className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-8 flex flex-col">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] flex items-center gap-3">
            <Smartphone size={30} strokeWidth={2.5} className="text-[#0056D2]" /> Det enkla mobilbytet
          </h2>
          <p className="font-heading text-3xl font-extrabold text-[#1A1A1A] mt-4">599 kr <span className="text-base font-medium text-[#4A4A4A]">fast pris</span></p>
          <p className="text-lg text-[#1A1A1A] mt-4">Har du köpt en ny telefon? Jag hjälper dig att flytta över allt tryggt och säkert! I tjänsten ingår flytt av:</p>
          <ul className="mt-4 space-y-3">
            {MOBILE_INCLUDED.map((f) => (
              <li key={f} className="flex items-start gap-3 text-lg text-[#1A1A1A]">
                <CheckCircle2 className="text-[#0B7A3F] shrink-0 mt-1" size={22} strokeWidth={2.5} /> {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Datorbytet */}
        <div data-testid="service-computer" className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-8 flex flex-col">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] flex items-center gap-3">
            <Laptop size={30} strokeWidth={2.5} className="text-[#0056D2]" /> Det enkla datorbytet
          </h2>
          <p className="font-heading text-3xl font-extrabold text-[#1A1A1A] mt-4">599 kr <span className="text-base font-medium text-[#4A4A4A]">fast pris</span></p>
          <p className="text-lg text-[#1A1A1A] mt-4">Har du köpt en ny dator? Jag ser till att du får med dig allt till din nya dator! I tjänsten ingår:</p>
          <ul className="mt-4 space-y-3">
            {COMPUTER_INCLUDED.map((f) => (
              <li key={f} className="flex items-start gap-3 text-lg text-[#1A1A1A]">
                <CheckCircle2 className="text-[#0B7A3F] shrink-0 mt-1" size={22} strokeWidth={2.5} /> {f}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {done ? (
        <div data-testid="mobile-success" className="bg-white border-2 border-gray-300 rounded-xl p-8 mt-8 text-center">
          <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4">
            <CheckCircle2 size={56} strokeWidth={2} />
          </span>
          <h2 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-5">Tack för din förfrågan!</h2>
          <p className="text-lg text-[#4A4A4A] mt-3">
            Jag har tagit emot dina uppgifter och återkommer så snart som möjligt för att boka en tid som passar dig.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-6">
            <HomeIcon size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A]">Boka ditt byte</h2>
          <p className="text-base text-[#4A4A4A] mt-2">Fyll i dina uppgifter så återkommer jag och bokar en tid. Det tar bara en minut.</p>

          <div className="space-y-6 mt-6">
            <div>
              <label htmlFor="m-service" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Vilken tjänst vill du boka?</label>
              <select id="m-service" data-testid="mobile-service" value={form.service} onChange={set("service")}
                className="min-h-[56px] text-lg px-3 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2] bg-white">
                <option value="">Välj tjänst…</option>
                <option value="Det enkla mobilbytet">Det enkla mobilbytet</option>
                <option value="Det enkla datorbytet">Det enkla datorbytet</option>
                <option value="Båda tjänsterna">Båda tjänsterna</option>
              </select>
            </div>
            <div>
              <label htmlFor="m-name" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Namn</label>
              <input id="m-name" data-testid="mobile-name" value={form.name} onChange={set("name")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="För- och efternamn" />
            </div>
            <div>
              <label htmlFor="m-email" className="text-xl font-bold mb-2 block text-[#1A1A1A]">E-post</label>
              <input id="m-email" type="email" data-testid="mobile-email" value={form.email} onChange={set("email")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="namn@exempel.se" />
            </div>
            <div>
              <label htmlFor="m-phone" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Telefonnummer</label>
              <input id="m-phone" type="tel" data-testid="mobile-phone" value={form.phone} onChange={set("phone")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="07X-XXX XX XX" />
            </div>
            <div>
              <label htmlFor="m-models" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Nuvarande och ny modell (mobil och/eller dator)</label>
              <input id="m-models" data-testid="mobile-models" value={form.phone_models} onChange={set("phone_models")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder='t.ex. iPhone 11 → iPhone 15, eller gammal PC → ny MacBook' />
            </div>
            <div>
              <label htmlFor="m-message" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Meddelande / Önskemål</label>
              <textarea id="m-message" data-testid="mobile-message" value={form.message} onChange={set("message")} rows={5}
                className="text-lg px-4 py-3 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2] resize-y"
                placeholder="Berätta gärna om du har särskilda önskemål (valfritt)" />
            </div>

            {error && (
              <p data-testid="mobile-error" className="flex items-center gap-2 text-[#D32F2F] text-lg font-semibold">
                <AlertCircle size={22} /> {error}
              </p>
            )}

            <button data-testid="mobile-submit" type="submit" disabled={submitting}
              className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60">
              {submitting ? <Loader2 className="animate-spin" size={26} /> : <Send size={26} strokeWidth={2.5} />}
              {submitting ? "Skickar…" : "Skicka förfrågan"}
            </button>

            <p data-testid="gdpr-notice" className="text-sm text-[#4A4A4A] leading-relaxed text-center">
              Din integritet är viktig för oss. Vi delar aldrig dina uppgifter med tredje part och hanterar all data säkert i enlighet med GDPR.
            </p>
          </div>
        </form>
      )}
    </div>
  );
}
